import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, CheckCircle2, AlertTriangle, Loader2, Upload, Shield,
  Building2, Calendar, Clock, XCircle, AlertCircle, FileCheck,
  ChevronRight, ExternalLink, Download, Eye, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "/logos/sterilemate-white.png?v=4";

// Required document types - Workers' Comp not required, Auto Insurance optional
const REQUIRED_DOCUMENTS = [
  {
    type: "trade_license",
    name: "Professional/Trade License",
    description: "Valid professional and/or trade licenses required to perform services",
    required: true
  },
  {
    type: "certifications",
    name: "Trade Certifications",
    description: "Required certifications (state, local, or trade-specific)",
    required: true
  },
  {
    type: "coi",
    name: "Certificate of Insurance (COI)",
    description: "General Liability: Min $1,000,000 per occurrence / $2,000,000 aggregate. Sterile Mate LLC must be named as Additional Insured.",
    required: true
  },
  {
    type: "workers_comp",
    name: "Workers' Compensation Insurance",
    description: "As required by state law (if applicable to your business)",
    required: false
  },
  {
    type: "auto_liability",
    name: "Automobile Liability Insurance",
    description: "Minimum $1,000,000 combined single limit (if using vehicles for work)",
    required: false
  },
  {
    type: "bond",
    name: "Performance/Payment Bond",
    description: "If required by project scope or client requirements",
    required: false
  },
  {
    type: "w9",
    name: "W-9 Tax Form",
    description: "Request for Taxpayer Identification Number and Certification",
    required: true
  }
];

export default function ContractorCompliancePage() {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [uploadData, setUploadData] = useState({
    file_url: "",
    document_name: "",
    expiration_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchComplianceData();
  }, [contractorId]);

  const fetchComplianceData = async () => {
    try {
      // Fetch compliance status
      const statusRes = await fetch(`${API}/contractor-compliance/status/${contractorId}`);
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setComplianceStatus(statusData);
      }

      // Fetch documents
      const docsRes = await fetch(`${API}/contractor-compliance/documents/${contractorId}`);
      if (docsRes.ok) {
        const docsData = await docsRes.json();
        setDocuments(docsData.documents || []);
      }
    } catch (err) {
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // For now, we'll create a placeholder URL
    // In production, this would upload to cloud storage
    const fileUrl = URL.createObjectURL(file);
    setUploadData(prev => ({
      ...prev,
      file_url: fileUrl,
      document_name: file.name
    }));
    toast.success("File selected. Please complete the form and submit.");
  };

  const submitDocument = async () => {
    if (!selectedDocType) {
      toast.error("Please select a document type");
      return;
    }
    if (!uploadData.file_url) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploading(true);
    try {
      const res = await fetch(`${API}/contractor-compliance/upload-document`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractor_id: contractorId,
          document_type: selectedDocType,
          document_name: uploadData.document_name || `${selectedDocType}_document`,
          file_url: uploadData.file_url,
          expiration_date: uploadData.expiration_date || null,
          notes: uploadData.notes || null
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Upload failed");
      }

      toast.success("Document uploaded successfully!");
      
      // Reset form
      setSelectedDocType(null);
      setUploadData({ file_url: "", document_name: "", expiration_date: "", notes: "" });
      
      // Refresh data
      fetchComplianceData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const res = await fetch(`${API}/contractor-compliance/document/${docId}`, {
        method: "DELETE"
      });

      if (res.ok) {
        toast.success("Document deleted");
        fetchComplianceData();
      }
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const getDocumentStatus = (docType) => {
    const doc = documents.find(d => d.document_type === docType);
    if (!doc) return "missing";
    if (doc.verified) return "verified";
    return "pending";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Sterile Mate" className="h-8" />
            <div>
              <h1 className="text-lg font-bold text-white">Compliance Documents</h1>
              <p className="text-xs text-slate-400">{complianceStatus?.contractor_company}</p>
            </div>
          </div>
          <Button onClick={() => navigate("/contractor/dashboard")} variant="outline" className="border-slate-600 text-slate-300">
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Compliance Progress */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Shield className="w-6 h-6 text-cyan-400" />
              Compliance Status
            </h2>
            <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
              complianceStatus?.is_compliant 
                ? "bg-emerald-500/20 text-emerald-400" 
                : "bg-amber-500/20 text-amber-400"
            }`}>
              {complianceStatus?.is_compliant ? "Fully Compliant" : "Action Required"}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Overall Compliance</span>
              <span className="text-white font-semibold">{complianceStatus?.compliance_percentage || 0}%</span>
            </div>
            <Progress value={complianceStatus?.compliance_percentage || 0} className="h-3" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={`p-3 rounded-lg ${complianceStatus?.agreement_signed ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <div className="flex items-center gap-2 mb-1">
                {complianceStatus?.agreement_signed ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400" />
                )}
                <span className="text-sm text-white">Agreement</span>
              </div>
              <p className="text-xs text-slate-400">
                {complianceStatus?.agreement_signed ? "Signed" : "Not Signed"}
              </p>
            </div>
            
            <div className="p-3 rounded-lg bg-slate-700/30 border border-slate-600">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-white">Documents</span>
              </div>
              <p className="text-xs text-slate-400">
                {complianceStatus?.documents_count || 0} uploaded
              </p>
            </div>
            
            <div className="p-3 rounded-lg bg-slate-700/30 border border-slate-600">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-white">Verified</span>
              </div>
              <p className="text-xs text-slate-400">
                {complianceStatus?.verified_documents_count || 0} verified
              </p>
            </div>
            
            {complianceStatus?.expired_documents_count > 0 && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-white">Expired</span>
                </div>
                <p className="text-xs text-red-400">
                  {complianceStatus?.expired_documents_count} expired
                </p>
              </div>
            )}
          </div>

          {/* Missing Documents Warning */}
          {complianceStatus?.missing_documents?.length > 0 && (
            <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <span className="text-white font-semibold">Missing Required Documents</span>
              </div>
              <ul className="text-sm text-amber-200 space-y-1">
                {complianceStatus.missing_documents.map((doc, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3" /> {doc}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Document Checklist */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Required Documents List */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-cyan-400" />
              Required Documents Checklist
            </h3>

            <div className="space-y-3">
              {REQUIRED_DOCUMENTS.map((docType) => {
                const status = getDocumentStatus(docType.type);
                const uploadedDoc = documents.find(d => d.document_type === docType.type);
                
                return (
                  <div 
                    key={docType.type}
                    className={`p-4 rounded-lg border ${
                      status === "verified" ? "bg-emerald-500/10 border-emerald-500/30" :
                      status === "pending" ? "bg-amber-500/10 border-amber-500/30" :
                      "bg-slate-700/30 border-slate-600"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {status === "verified" ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                        ) : status === "pending" ? (
                          <Clock className="w-5 h-5 text-amber-400 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-slate-500 mt-0.5" />
                        )}
                        <div>
                          <p className="text-white font-medium">
                            {docType.name}
                            {docType.required && <span className="text-red-400 ml-1">*</span>}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">{docType.description}</p>
                          
                          {uploadedDoc && (
                            <div className="mt-2 flex items-center gap-2 text-xs">
                              <span className="text-slate-400">Uploaded:</span>
                              <span className="text-white">{uploadedDoc.document_name}</span>
                              {uploadedDoc.expiration_date && (
                                <span className="text-amber-400">
                                  (Expires: {new Date(uploadedDoc.expiration_date).toLocaleDateString()})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {uploadedDoc && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(uploadedDoc.file_url, "_blank")}
                              className="text-slate-400 hover:text-white"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteDocument(uploadedDoc._id)}
                              className="text-slate-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {status === "missing" && (
                          <Button
                            size="sm"
                            onClick={() => setSelectedDocType(docType.type)}
                            className="bg-cyan-600 hover:bg-cyan-700 text-xs"
                          >
                            <Upload className="w-3 h-3 mr-1" /> Upload
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upload Form */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-400" />
              Upload Document
            </h3>

            {selectedDocType ? (
              <div className="space-y-4">
                <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <p className="text-white font-medium">
                    Uploading: {REQUIRED_DOCUMENTS.find(d => d.type === selectedDocType)?.name}
                  </p>
                </div>

                <div>
                  <Label className="text-slate-300">Select File *</Label>
                  <Input
                    type="file"
                    onChange={handleFileUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Accepted formats: PDF, JPG, PNG, DOC, DOCX
                  </p>
                </div>

                {uploadData.file_url && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <p className="text-emerald-400 text-sm flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      File selected: {uploadData.document_name}
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-slate-300">Document Name</Label>
                  <Input
                    value={uploadData.document_name}
                    onChange={(e) => setUploadData(prev => ({ ...prev, document_name: e.target.value }))}
                    placeholder="e.g., COI_2025.pdf"
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Expiration Date (if applicable)</Label>
                  <Input
                    type="date"
                    value={uploadData.expiration_date}
                    onChange={(e) => setUploadData(prev => ({ ...prev, expiration_date: e.target.value }))}
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-slate-300">Notes (optional)</Label>
                  <Input
                    value={uploadData.notes}
                    onChange={(e) => setUploadData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes..."
                    className="bg-slate-900 border-slate-600 text-white mt-1"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedDocType(null);
                      setUploadData({ file_url: "", document_name: "", expiration_date: "", notes: "" });
                    }}
                    className="border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitDocument}
                    disabled={uploading || !uploadData.file_url}
                    className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Document
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a document type from the checklist to upload</p>
              </div>
            )}
          </div>
        </div>

        {/* Uploaded Documents History */}
        {documents.length > 0 && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mt-8">
            <h3 className="text-lg font-bold text-white mb-4">Uploaded Documents History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Document</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Type</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Uploaded</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Expires</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc._id} className="border-b border-slate-700/50">
                      <td className="py-3 px-4 text-white">{doc.document_name}</td>
                      <td className="py-3 px-4 text-slate-300">
                        {REQUIRED_DOCUMENTS.find(d => d.type === doc.document_type)?.name || doc.document_type}
                      </td>
                      <td className="py-3 px-4 text-slate-400">
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-slate-400">
                        {doc.expiration_date ? new Date(doc.expiration_date).toLocaleDateString() : "-"}
                      </td>
                      <td className="py-3 px-4">
                        {doc.verified ? (
                          <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                            Verified
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs">
                            Pending Review
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(doc.file_url, "_blank")}
                          className="text-slate-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteDocument(doc._id)}
                          className="text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
